// AGC: FieldsList - Overview of all farms (fazendas)
// AGC: All farms are loaded from Firebase - filtered by user account
// AGC: New accounts start with empty data - no hardcoded mock data
// AGC: ⚠️ [DEPRECATED] This is a LEGACY view - LotesList is the main lot view
// AGC: ⚠️ [DEPRECATED] Only visible in dev mode (DEV_FLAGS.ENABLE_LEGACY_SCREENS)
import React, { useState, useEffect } from 'react'
import { Search, AlertTriangle } from 'lucide-react'
import { GlassCard, FloatingActionButton } from '../shared'
import { formatArea } from '@droidfarm/shared'
import { getFields } from '../../services/fields'
import { useAuth } from '../../hooks/useAuth'
import { getCropDisplayInfo, type Field, type Screen, type CropType } from '../../types'
import { useTranslation } from '../../hooks/useTranslation'
import { DEV_FLAGS } from '../../services/lotServices'

interface FieldsListProps {
  onNavigate: (screen: Screen, data?: { fieldId?: string }) => void
}

/**
 * AGC: FieldsList - Shows all farms with search and filter
 * AGC: Loads farms from Firebase for the authenticated user
 * AGC: ⚠️ DEPRECATED - This is a legacy component, LotesList is the primary lot management view
 * AGC: ⚠️ DEPRECATED - Only available in dev mode
 */
export const FieldsList: React.FC<FieldsListProps> = ({ onNavigate }) => {
  // AGC: [SOURCE_OF_TRUTH] Redirect to LotesList in production mode
  useEffect(() => {
    if (!DEV_FLAGS.ENABLE_LEGACY_SCREENS) {
      console.warn('⚠️ AGC: [DEPRECATED] FieldsList is deprecated. Redirecting to lotes-list...')
      onNavigate('lotes-list')
    }
  }, [onNavigate])

  // AGC: Show deprecation warning in dev mode
  if (DEV_FLAGS.ENABLE_LEGACY_SCREENS) {
    console.warn('⚠️ AGC: [DEPRECATED] FieldsList is a legacy component. Use LotesList instead.')
  }
  const { getAuthToken } = useAuth()
  const { t } = useTranslation()
  const [fields, setFields] = useState<Field[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<CropType | 'all'>('all')

  // AGC: Load fields from Firebase for the authenticated user
  useEffect(() => {
    const loadFields = async () => {
      try {
        setIsLoading(true)
        const token = await getAuthToken()
        const data = await getFields(token)
        setFields(data)
      } catch (error) {
        console.error('AGC: Error loading fields:', error)
        setFields([]) // AGC: New accounts start with empty data
      } finally {
        setIsLoading(false)
      }
    }

    loadFields()
  }, [getAuthToken])

  // AGC: Filter fields based on search and crop filter
  const filteredFields = fields.filter(field => {
    const matchesSearch = field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          field.location.toLowerCase().includes(searchQuery.toLowerCase())
    
    if (selectedFilter === 'all') return matchesSearch
    
    const hasCrop = field.plots?.some(plot => plot.cropType === selectedFilter) || false
    return matchesSearch && hasCrop
  })

  // AGC: Filter chips for crop types
  const filterChips: { id: CropType | 'all'; label: string }[] = [
    { id: 'all', label: 'Todos' },
    { id: 'soja', label: 'Soja' },
    { id: 'milho', label: 'Milho' },
    { id: 'trigo', label: 'Trigo' },
  ]

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-24">
      {/* AGC: Header - Search and filters (not sticky, TopNav handles that) */}
      <header className="bg-white dark:bg-slate-900 px-4 pt-4 pb-4">
        <h1 className="text-gray-900 dark:text-gray-100 mb-4">{t('entity.farm_plural')}</h1>
        
        {/* AGC: Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('fields.searchFields')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>

        {/* AGC: Filter Chips */}
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          {filterChips.map((chip) => (
            <button
              key={chip.id}
              onClick={() => setSelectedFilter(chip.id)}
              className={`chip whitespace-nowrap ${selectedFilter === chip.id ? 'active' : ''}`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </header>

      {/* AGC: Farms List */}
      <div className="px-4 py-4 space-y-3">
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('fields.loadingFarms')}</p>
          </div>
        ) : filteredFields.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-2">{t('fields.noFields')}</p>
            <p className="text-sm text-gray-400">{t('fields.addFirstField')}</p>
          </div>
        ) : (
          filteredFields.map((field) => (
            <FieldCard
              key={field.id}
              field={field}
              onClick={() => onNavigate('field-detail', { fieldId: field.id })}
              t={t}
            />
          ))
        )}
      </div>

      {/* AGC: FAB - Add new plot */}
      {/* AGC: Removed duplicate BottomNav - MainMenu handles navigation */}
      <FloatingActionButton onClick={() => onNavigate('new-plot-1')} />
    </div>
  )
}

/**
 * AGC: Field Card Component (shows farm/fazenda info)
 */
interface FieldCardProps {
  field: Field
  onClick: () => void
  t: (key: string) => string
}

const FieldCard: React.FC<FieldCardProps> = ({ field, onClick, t }) => {
  // AGC: Count crops by type (handle empty plots array)
  const plots = field.plots || []
  const cropCounts = plots.reduce((acc, plot) => {
    acc[plot.cropType] = (acc[plot.cropType] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  return (
    <GlassCard onClick={onClick} hover padding="none">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-gray-900 dark:text-gray-100">{field.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{field.location}</p>
          </div>
          <span className="text-sm font-medium text-[#0B5F4A]">
            {formatArea(field.totalArea)}
          </span>
        </div>

        {/* AGC: Lots (talhões) info */}
        <div className="flex items-center gap-4 mt-3">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">{plots.length}</span> {plots.length === 1 ? t('entity.lot').toLowerCase() : t('entity.lot_plural').toLowerCase()}
          </div>
          
          {/* AGC: Crop badges */}
          {Object.keys(cropCounts).length > 0 && (
            <div className="flex gap-1">
              {Object.entries(cropCounts).slice(0, 3).map(([crop, count]) => {
                const cropDisplay = getCropDisplayInfo(crop)
                return (
                  <span
                    key={crop}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                    style={{ 
                      backgroundColor: `${cropDisplay.color}20`,
                      color: cropDisplay.color
                    }}
                  >
                    {cropDisplay.icon} {count}
                  </span>
                )
              })}
            </div>
          )}
        </div>

        {/* AGC: Visual plot grid preview */}
        {plots.length > 0 && (
          <div className="mt-3 flex gap-1">
            {plots.slice(0, 6).map((plot, idx) => {
              const cropDisplay = getCropDisplayInfo(plot.cropType)
              return (
                <div
                  key={plot.id}
                  className="h-2 rounded-full flex-1"
                  style={{ backgroundColor: cropDisplay.color }}
                  title={plot.name}
                />
              )
            })}
            {plots.length > 6 && (
              <div className="h-2 rounded-full flex-1 bg-gray-300" />
            )}
          </div>
        )}
      </div>
    </GlassCard>
  )
}

export default FieldsList



